import React from 'react';
import './mirea-parts.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const months = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]}`;
}

function moodleHref(retake) {
  if (!retake?.moodle_course_slug) return null;
  const anchor = retake.moodle_topic_anchor ? `#${retake.moodle_topic_anchor}` : '';
  return `moodle://course/${retake.moodle_course_slug}${anchor}`;
}

export function postMoodleNav(href) {
  if (!href) return;
  const msg = { type: 'focusml-moodle-nav', href };
  window.postMessage(msg, '*');
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(msg, '*');
  }
}

export function openSourcePreview(rowIds, apiBase) {
  if (!rowIds?.length) return;
  const base = (apiBase || '/api/demo/mirea/retakes').replace(/\/$/, '');
  const url = `${base}/source-preview?row_ids=${rowIds.join(',')}`;
  window.postMessage({ type: 'focusml-source-preview', url }, '*');
}

function collectSourceRowIds(items) {
  const ids = new Set();
  (items || []).forEach((r) => {
    (r.source_row_ids || r.sourceRowIds || [r.row_id]).forEach((id) => {
      if (id != null) ids.add(id);
    });
  });
  return [...ids];
}

function primaryGroupFromItems(items) {
  for (const item of items || []) {
    if (item.group) return item.group;
    const groups = item.groups;
    if (Array.isArray(groups) && groups[0]) return groups[0];
  }
  return null;
}

/** Normalize timeline props (camel + snake) and fill gaps from row data. */
export function normalizeRetakeTimelineProps(props = {}) {
  const items = props.items || props.retakes || [];
  const group =
    props.group
    || props.exportGroup
    || props.export_group
    || primaryGroupFromItems(items)
    || 'КТСО-23';
  const exportGroup = props.exportGroup || props.export_group || group;
  const apiBase = props.apiBase || props.api_base || '/api/demo/mirea/retakes';
  const sourceRowIds = props.sourceRowIds || props.source_row_ids || collectSourceRowIds(items);
  const exportKind = props.exportKind || props.export_kind || 'group';
  const teacher = props.teacher || '';
  const date = props.date || '';
  return {
    ...props,
    items,
    group,
    exportGroup,
    exportKind,
    teacher,
    date,
    apiBase,
    sourceRowIds,
    sourceLabel: props.sourceLabel || props.source_label || '',
  };
}

/** Keep richer streamed metadata when done payload thins timeline props. */
export function mergeRetakeTimelineFromStream(finalParts, streamedParts) {
  const parts = Array.isArray(finalParts) ? finalParts : [];
  const streamed = (streamedParts || []).find((p) => p.component === 'retake_timeline');
  if (!streamed) return parts;
  return parts.map((p) => {
    if (p.component !== 'retake_timeline') return p;
    return {
      ...streamed,
      ...p,
      props: normalizeRetakeTimelineProps({
        ...(streamed.props || {}),
        ...(p.props || {}),
        items: (p.props || {}).items || (p.props || {}).retakes || (streamed.props || {}).items || (streamed.props || {}).retakes,
      }),
    };
  });
}

function RetakeCardCompact({ retake, dir }) {
  const r = retake || {};
  const href = moodleHref(r);
  const timeRange = r.time_end ? `${r.time_start}–${r.time_end}` : r.time_start;
  const metaLine = [r.teacher, r.room ? `Ауд. ${r.room}` : null].filter(Boolean).join(', ');

  return (
    <div className="mirea-retake-row" dir={dir}>
      <div className="mirea-retake-row__main">
        <div className="mirea-retake-row__top">
          {href ? (
            <button
              type="button"
              className="mirea-retake-row__title mirea-retake-row__title--link"
              data-moodle-href={href}
              onClick={() => postMoodleNav(href)}
            >
              {r.discipline}
            </button>
          ) : (
            <span className="mirea-retake-row__title">{r.discipline}</span>
          )}
          {r.type && <span className="mirea-retake-row__badge">{r.type}</span>}
        </div>
        <div className="mirea-retake-row__sub">
          {r.date && (
            <span>
              {formatDate(r.date)}
              {timeRange ? `, ${timeRange}` : ''}
            </span>
          )}
          {metaLine && <span className="mirea-retake-row__meta">{metaLine}</span>}
        </div>
      </div>
    </div>
  );
}

/** Action bar rendered below the message bubble (imported by widget). */
export function MireaTimelineFooter({
  items = [],
  group,
  exportGroup,
  apiBase,
  exportKind = 'group',
  teacher,
  date,
}) {
  const base = (apiBase || '/api/demo/mirea/retakes').replace(/\/$/, '');
  const kind = exportKind || 'group';
  const firstItem = items[0];
  const firstId = firstItem?.id || (firstItem?.row_id != null ? `row_${firstItem.row_id}` : null);

  if (!items.length && kind !== 'students') return null;
  if (kind === 'students' && !teacher) return null;

  const dateQ = date ? `?date=${encodeURIComponent(date)}` : '';
  const teacherEnc = encodeURIComponent(teacher || '');

  if (kind === 'students') {
    return (
      <div className="mirea-timeline-footer">
        {firstId && (
          <a
            className="mirea-btn mirea-btn--primary mirea-btn--block"
            href={`${base}/retakes/${encodeURIComponent(firstId)}/calendar.ics`}
            download
          >
            Добавить в календарь
          </a>
        )}
        <div className="mirea-timeline-footer__row">
          <a
            className="mirea-btn mirea-btn--half"
            href={`${base}/teachers/${teacherEnc}/students.pdf${dateQ}`}
            target="_blank"
            rel="noreferrer"
          >
            Распечатать
          </a>
          <a
            className="mirea-btn mirea-btn--half"
            href={`${base}/teachers/${teacherEnc}/students.xlsx${dateQ}`}
            download
          >
            Excel
          </a>
        </div>
      </div>
    );
  }

  const exportG = exportGroup || group || primaryGroupFromItems(items) || 'КТСО-23';
  const scheduleBase = kind === 'teacher'
    ? `${base}/teachers/${encodeURIComponent(teacher || exportG)}`
    : `${base}/groups/${encodeURIComponent(exportG)}`;

  if (!items.length) return null;

  return (
    <div className="mirea-timeline-footer">
      {firstId && (
        <a
          className="mirea-btn mirea-btn--primary mirea-btn--block"
          href={`${base}/retakes/${encodeURIComponent(firstId)}/calendar.ics`}
          download
        >
          Добавить в календарь
        </a>
      )}
      <div className="mirea-timeline-footer__row">
        <a
          className="mirea-btn mirea-btn--half"
          href={`${scheduleBase}/schedule.pdf`}
          target="_blank"
          rel="noreferrer"
        >
          Распечатать
        </a>
        <a
          className="mirea-btn mirea-btn--half"
          href={`${scheduleBase}/schedule.xlsx`}
          download
        >
          Excel
        </a>
      </div>
    </div>
  );
}

/** PDF source chip — rendered inline after assistant text. */
export function MireaSourcePdfChip({ rowIds, apiBase }) {
  if (!rowIds?.length) return null;
  const base = (apiBase || '/api/demo/mirea/retakes').replace(/\/$/, '');
  return (
    <button
      type="button"
      className="mirea-source-chip mirea-source-chip--pdf mirea-source-chip--inline"
      onClick={() => openSourcePreview(rowIds, base)}
    >
      PDF
    </button>
  );
}

export function RetakeCard({ props: p, dir }) {
  return <RetakeCardCompact retake={p.retake} dir={dir} />;
}

export function RetakeTimeline({ props: p, dir }) {
  const items = p.items || p.retakes || [];
  return (
    <div className="mirea-timeline mirea-timeline--compact" dir={dir}>
      {items.map((r) => (
        <RetakeCardCompact key={r.id || r.row_id} retake={r} dir={dir} />
      ))}
    </div>
  );
}

export function RetakeConflicts({ props: p, dir }) {
  const conflicts = p.conflicts || [];
  if (!conflicts.length) {
    return (
      <div className="mirea-conflicts mirea-conflicts--ok" dir={dir}>
        Конфликтов по расписанию не обнаружено.
      </div>
    );
  }
  return (
    <div className="mirea-conflicts" dir={dir}>
      {conflicts.map((c) => (
        <div key={`${c.date}-${c.time_start}`} className="mirea-conflicts__item">
          <strong>
            {formatDate(c.date)}, {c.time_start}
            {c.room ? ` · ${c.room}` : ''}
          </strong>
          <ul>
            {(c.disciplines || []).map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function RetakeExportBar({ props: p, dir }) {
  const group = p.group || 'КТСО-23';
  const apiBase = (p.apiBase || '/api/demo/mirea/retakes').replace(/\/$/, '');
  return (
    <div className="mirea-export-bar" dir={dir}>
      <a className="mirea-btn mirea-btn--primary mirea-btn--block" href={`${apiBase}/groups/${encodeURIComponent(group)}/schedule.pdf`} target="_blank" rel="noreferrer">
        Распечатать
      </a>
      <a className="mirea-btn mirea-btn--block" href={`${apiBase}/groups/${encodeURIComponent(group)}/schedule.xlsx`} download>
        Excel
      </a>
    </div>
  );
}

export function getMireaTimelineFooterProps(uiParts) {
  const part = (uiParts || []).find((p) => p.component === 'retake_timeline');
  if (!part?.props) return null;
  const normalized = normalizeRetakeTimelineProps(part.props);
  if (!normalized.items.length) return null;
  return {
    items: normalized.items,
    group: normalized.group,
    exportGroup: normalized.exportGroup,
    exportKind: normalized.exportKind,
    teacher: normalized.teacher,
    date: normalized.date,
    apiBase: normalized.apiBase,
    sourceRowIds: normalized.sourceRowIds,
    sourceLabel: normalized.sourceLabel,
  };
}

function normalizeDataTableFooterProps(props = {}) {
  const exportKind = props.exportKind || props.export_kind;
  if (exportKind !== 'students' || !props.rows?.length) return null;
  const teacher = props.teacher || '';
  if (!teacher) return null;
  const retakes = props.retakes || [];
  return {
    items: retakes,
    exportKind: 'students',
    teacher,
    date: props.date || '',
    apiBase: props.apiBase || props.api_base || '/api/demo/mirea/retakes',
    sourceRowIds: props.sourceRowIds || props.source_row_ids || [],
  };
}

/** Footer props from timeline or professor student table. */
export function getMireaFooterProps(uiParts) {
  const timeline = getMireaTimelineFooterProps(uiParts);
  if (timeline) return timeline;
  const tablePart = (uiParts || []).find((p) => p.component === 'data_table');
  return normalizeDataTableFooterProps(tablePart?.props);
}

/** PDF chip source rows from timeline or student table. */
export function getMireaPdfChipProps(uiParts) {
  const footer = getMireaFooterProps(uiParts);
  if (footer?.sourceRowIds?.length) {
    return { rowIds: footer.sourceRowIds, apiBase: footer.apiBase };
  }
  const tablePart = (uiParts || []).find((p) => p.component === 'data_table');
  const ids = tablePart?.props?.sourceRowIds || tablePart?.props?.source_row_ids;
  if (ids?.length) {
    return {
      rowIds: ids,
      apiBase: tablePart.props.apiBase || tablePart.props.api_base || '/api/demo/mirea/retakes',
    };
  }
  return null;
}
